"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { format } from "date-fns";
import { MapPin, Calendar as CalendarIcon, Users, Search, Ship, TreePalm, Minus, Plus, Mail, Phone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const PassengerStepper = ({
  label,
  value,
  onChange,
  min = 0,
  max = 10
}: {
  label: string;
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
}) => {
  return <div data-section-id="53952" className="flex items-center justify-between py-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full border-primary/20 text-primary hover:bg-primary/10 hover:text-primary" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>
          <Minus className="size-3" />
        </Button>
        <span className="w-4 text-center text-sm font-semibold">{value}</span>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full border-primary/20 text-primary hover:bg-primary/10 hover:text-primary" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>
          <Plus className="size-3" />
        </Button>
      </div>
    </div>;
};
function BookingFormContent() {
  const {
    control,
    isSubmitting,
    handleSubmit
  } = useWvcForm();
  const [serviceType, setServiceType] = useState("ferry");
  const adults = useWatch({
    control,
    name: "adults"
  }) || 1;
  const childrenCount = useWatch({
    control,
    name: "children"
  }) || 0;
  const totalPassengers = (Number(adults) || 0) + (Number(childrenCount) || 0);
  return <div className="relative">
      {}
      <div className="mb-6 flex justify-center md:justify-start">
        <ToggleGroup type="single" value={serviceType} onValueChange={val => val && setServiceType(val)} className="bg-white/40 p-1 backdrop-blur-md rounded-lg border border-white/30 shadow-sm">
          <ToggleGroupItem value="ferry" className="rounded-md px-6 data-[state=on]:bg-primary data-[state=on]:text-foreground-primary transition-all font-medium">
            <Ship className="mr-2 size-4" />
            Luxury Sedan
          </ToggleGroupItem>
          <ToggleGroupItem value="excursions" className="rounded-md px-6 data-[state=on]:bg-primary data-[state=on]:text-foreground-primary transition-all font-medium">
            <TreePalm className="mr-2 size-4" />
            Premium SUV
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <Form className="space-y-4" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          {}
          
          {}
          <div className="lg:col-span-4">
            <FormField control={control} name="destination" rules={{
            required: "Pickup location is required"
          }} render={({
            field
          }) => <FormItem className="space-y-0">
                  <FormControl>
                    <div className="relative group">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-primary">
                        <MapPin className="size-5" />
                      </div>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <SelectTrigger className="h-14 w-full border-0 bg-white/60 pl-10 text-left text-base font-medium shadow-sm backdrop-blur-md transition-all hover:bg-white/80 focus:ring-2 focus:ring-primary/40">
                          <SelectValue placeholder="Select Pickup Area" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lax">LAX International Airport</SelectItem>
                          <SelectItem value="beverly-hills">Beverly Hills</SelectItem>
                          <SelectItem value="santa-monica">Santa Monica</SelectItem>
                          <SelectItem value="burbank">Burbank Airport (BUR)</SelectItem>
                          <SelectItem value="downtown">Downtown Los Angeles</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </FormControl>
                  <FormMessage className="absolute text-xs mt-1" />
                </FormItem>} />
          </div>

          {}
          <div className="lg:col-span-3">
            <FormField control={control} name="check_in" rules={{
            required: "Pickup date is required"
          }} render={({
            field
          }) => <FormItem className="space-y-0">
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant="outline" className={cn("h-14 w-full justify-start border-0 bg-white/60 pl-3 text-left font-normal shadow-sm backdrop-blur-md hover:bg-white/80 focus:ring-2 focus:ring-primary/40", !field.value && "text-muted-foreground")}>
                          <SafeHydrate><CalendarIcon className="mr-2 size-5 text-primary" /></SafeHydrate>
                          <div className="flex flex-col items-start text-sm">
                            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">Pickup Date</span>
                            <span className="font-semibold text-foreground">
                              {field.value ? format(new Date(field.value), "MMM dd, yyyy") : <span>Select Date</span>}
                            </span>
                          </div>
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date?.toISOString())} disabled={date => date < new Date()} initialFocus /></SafeHydrate>
                    </PopoverContent>
                  </Popover>
                  <FormMessage className="absolute text-xs mt-1" />
                </FormItem>} />
          </div>

          {}
          <div className="lg:col-span-3">
            <FormField control={control} name="check_out" rules={{
            required: "Return date is required"
          }} render={({
            field
          }) => <FormItem className="space-y-0">
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant="outline" className={cn("h-14 w-full justify-start border-0 bg-white/60 pl-3 text-left font-normal shadow-sm backdrop-blur-md hover:bg-white/80 focus:ring-2 focus:ring-primary/40", !field.value && "text-muted-foreground")}>
                          <SafeHydrate><CalendarIcon className="mr-2 size-5 text-primary" /></SafeHydrate>
                          <div className="flex flex-col items-start text-sm">
                            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">Return Date</span>
                            <span className="font-semibold text-foreground">
                              {field.value ? format(new Date(field.value), "MMM dd, yyyy") : <span>Select Date</span>}
                            </span>
                          </div>
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date?.toISOString())} disabled={date => date < new Date()} initialFocus /></SafeHydrate>
                    </PopoverContent>
                  </Popover>
                  <FormMessage className="absolute text-xs mt-1" />
                </FormItem>} />
          </div>

          {}
          <div className="lg:col-span-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-14 w-full justify-start border-0 bg-white/60 pl-3 text-left font-normal shadow-sm backdrop-blur-md hover:bg-white/80 focus:ring-2 focus:ring-primary/40">
                  <Users className="mr-2 size-5 text-primary" />
                  <div className="flex flex-col items-start text-sm">
                    <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">Guests</span>
                    <span className="font-semibold text-foreground">
                      {totalPassengers} Passenger{totalPassengers !== 1 && "s"}
                    </span>
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="end">
                <div className="space-y-4">
                  <h4 className="font-display font-semibold leading-none text-foreground">Passenger Details</h4>
                  <div className="grid gap-2">
                    <FormField control={control} name="adults" render={({
                    field
                  }) => <PassengerStepper label="Adults" value={Number(field.value) || 1} onChange={field.onChange} min={1} />} />
                    <FormField control={control} name="children" render={({
                    field
                  }) => <PassengerStepper label="Children" value={Number(field.value) || 0} onChange={field.onChange} min={0} />} />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          {}
          <div className="lg:col-span-4">
            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem className="space-y-0">
                  <FormControl>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-primary">
                        <Mail className="size-5" />
                      </div>
                      <Input {...field} type="email" placeholder="Email Address" className="h-12 border-0 bg-white/60 pl-10 shadow-sm backdrop-blur-md hover:bg-white/80 focus-visible:ring-2 focus-visible:ring-primary/40" />
                    </div>
                  </FormControl>
                  <FormMessage className="absolute text-xs mt-1" />
                </FormItem>} />
          </div>

          {}
          <div className="lg:col-span-4">
            <FormField control={control} name="phone" rules={{
            required: "Phone number is required"
          }} render={({
            field
          }) => <FormItem className="space-y-0">
                  <FormControl>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-primary">
                        <Phone className="size-5" />
                      </div>
                      <Input {...field} type="tel" placeholder="Phone Number" className="h-12 border-0 bg-white/60 pl-10 shadow-sm backdrop-blur-md hover:bg-white/80 focus-visible:ring-2 focus-visible:ring-primary/40" />
                    </div>
                  </FormControl>
                  <FormMessage className="absolute text-xs mt-1" />
                </FormItem>} />
          </div>

          {}
          <div className="lg:col-span-4">
            <Button type="submit" disabled={isSubmitting} className="h-12 w-full bg-primary text-sm font-bold uppercase tracking-widest text-foreground-primary shadow-strong transition-all hover:bg-primary-hover hover:shadow-primary/25 hover:-translate-y-0.5">
              {isSubmitting ? "Processing..." : <>
                  <Search className="mr-2 size-5" />
                  Book Your Ride
                </>}
            </Button>
          </div>
        </div>
      </Form>
    </div>;
}
export default function Booking() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="bookingform" className="relative w-full overflow-hidden bg-background py-16 lg:py-24">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/80 via-primary-dark/40 to-primary-dark/90 z-10" />
        <div className="h-full w-full bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/maldives_ferry_terminal.png)] bg-cover bg-center opacity-40" />
      </div>

      <div className="container relative z-20 mx-auto px-4">
        <div ref={ref} className={cn("mx-auto max-w-6xl transition-all duration-1000 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0")}>
          <div className="mb-10 text-center">
            <h2 className="text-4xl font-display font-bold tracking-tight text-white md:text-5xl lg:text-6xl">
              Reserve Your Chauffeur
            </h2>
            <p className="mt-4 text-xl text-white/80 font-medium">
              Elite black car and SUV service for Los Angeles, airport transfers, and VIP events.
            </p>
          </div>

          {}
          <div className="rounded-xl border border-white/20 bg-white/10 p-6 shadow-strong backdrop-blur-xl md:p-8 lg:p-10">
            <FormProvider formKey="booking_form_home" formId="booking_form_home" sectionName="booking-section" defaultValues={{
            destination: "",
            check_in: "",
            check_out: "",
            adults: 1,
            children: 0,
            email: "",
            phone: ""
          }} validationRules={{
            destination: {
              required: true
            },
            check_in: {
              required: true
            },
            check_out: {
              required: true
            },
            adults: {
              min: 1
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            }
          }} submitText="Book Your Ride" successMessage="Your reservation request has been received. Our team will contact you shortly to confirm.">
              <BookingFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}